import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Form,
  Radio,
  Alert,
  Checkbox,
  InputRef,
} from "antd";
import Header from "./Header";
import "./AgentManageModel.css"; // Import CSS file for styling
import { PatchAgentFormData } from "./types";
import InputMask from "react-input-mask";

interface ManageAgentModalProps {
  agent: any;
  visible: boolean;
  formData: PatchAgentFormData;
  existingEmails: string[];
  existingPhones: string[];
  onFormDataChange: (data: Partial<PatchAgentFormData>) => void;
  onClose: () => void;
  onPatchAgent: (updatedProperties: any) => Promise<void>;
}

const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "").slice(0, 10);
  return cleaned;
};

const ManageAgentModal: React.FC<ManageAgentModalProps> = ({
  agent,
  visible,
  onClose,
  onPatchAgent,
  formData,
  onFormDataChange,
  existingEmails,
  existingPhones,
}) => {
  const { first_name, last_name, email, phone_number, status } = formData;
  const [errors, setErrors] = useState<Partial<PatchAgentFormData>>({});
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);

  useEffect(() => {
    if (agent && visible) {
      onFormDataChange({
        first_name: agent.first_name,
        last_name: agent.last_name,
        email: agent.email,
        phone_number: agent.phone_number,
        status: agent.status,
      });
    }
    setErrors({});
  }, [agent, visible]);

  const isSaveDisabled =
    Object.values(errors).some((error) => error) ||
    (agent?.have_login && !acknowledgeChecked);

  const isEmailValid = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isPhoneValid = (phone: string) => {
    const regex = /^\d{10}$/;
    return regex.test(phone);
  };

  const handleInputChange = (
    key: keyof PatchAgentFormData,
    value: string | boolean
  ) => {
    // Update form data
    onFormDataChange({ ...formData, [key]: value });

    // Validate email
    if (key === "email" && !isEmailValid(value as string)) {
      setErrors({ ...errors, [key]: "Invalid email format" });
    } else if (
      key === "email" &&
      existingEmails.some(
        (email) => email.toLowerCase() === (value as string).toLowerCase()
      )
    ) {
      setErrors({
        ...errors,
        [key]: "A user with the same email already exists",
      });
    } else if (key === "email") {
      setErrors({ ...errors, [key]: undefined });
    }

    // Validate phone
    if (key === "phone_number" && !isPhoneValid(value as string)) {
      setErrors({ ...errors, [key]: "Invalid phone number" });
    } else if (existingPhones.includes(value as string)) {
      setErrors({
        ...errors,
        [key]: "A user with same phone number already exists",
      });
    } else if (key === "phone_number") {
      setErrors({ ...errors, [key]: undefined });
    }

    // Validate firstName
    if (
      key === "first_name" &&
      (!value || typeof value !== "string" || value.length < 1)
    ) {
      setErrors({ ...errors, [key]: "First name is required" });
    } else if (key === "first_name") {
      setErrors({ ...errors, [key]: undefined });
    }

    // Validate lastName
    if (
      key === "last_name" &&
      (!value || typeof value !== "string" || value.length < 1)
    ) {
      setErrors({ ...errors, [key]: "Last name is required" });
    } else if (key === "last_name") {
      setErrors({ ...errors, [key]: undefined });
    }
  };

  const handleStatusToggle = (status: string) => {
    onFormDataChange({ ...formData, status });
  };

  const firstNameRef = useRef<InputRef>(null);
  const lastNameRef = useRef<InputRef>(null);
  const emailRef = useRef<InputRef>(null);
  const phoneRef = useRef<InputRef>(null);

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    nextRef: React.RefObject<InputRef>
  ) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      nextRef.current?.focus();
    }
  };

  const findUpdatedProperties = (agent: any, formData: any) => {
    const updatedProperties: any = {};
    for (const key in formData) {
      if (formData.hasOwnProperty(key) && agent.hasOwnProperty(key)) {
        if (formData[key] !== agent[key]) {
          updatedProperties[key] = formData[key];
        }
      }
    }
    return updatedProperties;
  };

  const handlePatch = () => {
    const updatedProperties = findUpdatedProperties(agent, formData);
    onPatchAgent(updatedProperties);
  };

  return (
    <Modal
      title={
        <Header
          header="Manage Agent"
          subheader="View your details, assigned roles, and permissions. You can also reset your password here."
        />
      }
      open={visible}
      footer={null}
      width={800}
      closeIcon={null}
      className="no-padding-modal"
      onCancel={onClose}
      maskClosable={false}
    >
      <div className="modal-content">
        <div style={{ padding: "0px 5px" }}>
          <h3>Step 1: Modify Agent Details</h3>
          <p>
            Modify basic details of the agent.{" "}
            <small>Email address and phone number should be unique.</small>
          </p>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <div className={errors.first_name ? "field-error" : ""}>
              <label>First Name</label>
              <Input
                ref={firstNameRef}
                autoComplete="new-password"
                placeholder="First Name"
                value={first_name}
                onChange={(e) =>
                  handleInputChange("first_name", e.target.value)
                }
                onKeyDown={(e) => handleKeyPress(e, lastNameRef)}
                tabIndex={1}
              />
            </div>
            <div className={errors.last_name ? "field-error" : ""}>
              <label>Last Name</label>
              <Input
                ref={lastNameRef}
                autoComplete="new-password"
                placeholder="Last Name"
                value={last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                onKeyDown={(e) => handleKeyPress(e, emailRef)}
                tabIndex={2}
              />
            </div>
            <div className={errors.email ? "field-error" : ""}>
              <label>Email</label>
              <Input
                ref={emailRef}
                autoComplete="new-password"
                placeholder="Email"
                value={email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                onKeyDown={(e) => handleKeyPress(e, phoneRef)}
                tabIndex={3}
              />
              <div style={{ color: "#af6465", marginTop: "5px" }}>
                {errors["email"]}
              </div>
            </div>
            <div className={errors.phone_number ? "field-error" : ""}>
              <label>Phone</label>
              <InputMask
                mask="(999) 999-9999"
                value={phone_number}
                onChange={(e) =>
                  handleInputChange(
                    "phone_number",
                    formatPhoneNumber(e.target.value)
                  )
                }
                tabIndex={4}
                className="ant-input css-dev-only-do-not-override-1mmediq ant-input-outlined"
              >
                {/* {
                  ((inputProps: any) => (
                    <Input
                      {...inputProps}
                      ref={phoneRef}
                      autoComplete="new-password"
                      placeholder="Phone"
                    />
                  )) as unknown as React.ReactNode
                } */}
              </InputMask>
              <div style={{ color: "#af6465", marginTop: "5px" }}>
                {errors["phone_number"]}
              </div>
            </div>

            <Alert
              message="Changing the status will keep the agent in network but agent features will be disabled."
              type="info"
              showIcon
              closable
            />
            <div>
              <Radio.Group
                onChange={(e) => handleStatusToggle(e.target.value)}
                value={status}
              >
                <Radio value="active" tabIndex={5}>
                  Active
                </Radio>
                <Radio value="inactive" tabIndex={6}>
                  Inactive
                </Radio>
              </Radio.Group>
            </div>
          </div>
        </div>
        <div style={{ padding: "0px 5px" }}>
          {agent?.have_login && (
            <div>
              <h3>Step 2: Acknowledge changes to connected Login</h3>
              <p>
                The user record connected to the agent will be modified based on
                the change made to the agent record.
              </p>
              <Checkbox
                onChange={(e) => setAcknowledgeChecked(e.target.checked)}
              >
                <b>Acknowledged</b>
              </Checkbox>
            </div>
          )}
        </div>
        <div className="button-group">
          <Button
            key="cancel"
            onClick={onClose}
            style={{ marginRight: "10px" }}
          >
            Cancel
          </Button>
          <Button
            key="save"
            type="primary"
            htmlType="submit"
            onClick={handlePatch}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ManageAgentModal;
